import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { IChartApi, ISeriesApi } from 'lightweight-charts';
import { Observable, Subject } from 'rxjs';
import {
  DataFeedResponse,
  Timeframe
} from '@server/data-feed/schemas/output.schema';
import { EntryTrigger } from '../hooks/chart-data.hook';
import { IndicatorConfigs } from '../types/indicators.types';

type MarketAnalysisData = {
  signals: {
    raw: Timeframe['signals'];
    triggers: {
      long: EntryTrigger[];
      short: EntryTrigger[];
    };
    triggersConfluence: {
      long: EntryTrigger[];
      short: EntryTrigger[];
    };
  };
};

type TradeExecutionData = {
  entries: DataFeedResponse['trades'];
  adds: DataFeedResponse['trades'];
  exits: DataFeedResponse['trades'];
  stopLosses: DataFeedResponse['trades'];
  takeProfits: DataFeedResponse['trades'];
};

export type TradingChartData = {
  analysis: MarketAnalysisData;
  execution: TradeExecutionData;
  indicatorConfigs: IndicatorConfigs;
};

export type ChartContext = {
  chartContainerRef: React.RefObject<HTMLDivElement | null>;
  chartRef: React.RefObject<IChartApi | null>;
  candlestickSeriesRef: React.RefObject<ISeriesApi<'Candlestick'> | null>;
  overlayIndicatorSeriesRefs: React.RefObject<
    Partial<Record<string, any[] | null>>
  >;
  indicatorContainerRefs: React.RefObject<Record<
    string,
    HTMLDivElement
  > | null>;
  indicatorChartRefs: React.RefObject<Record<string, IChartApi[]>>;
  indicatorSeriesRefs: React.RefObject<Partial<Record<string, any[] | null>>>;
  tooltipRef: React.RefObject<HTMLDivElement | null>;
  initialLoad: boolean;
  chartInitialized: boolean;
  setContext: (value: Partial<ChartContext>) => void;
  subplotChartCreated$: Observable<void>;
  emitSubplotChartCreated: () => void;
  data: TradingChartData;
};

export const ChartContextInstance = createContext<ChartContext | undefined>(
  undefined
);

export const useChartContext = () => {
  const context = useContext(ChartContextInstance);

  if (!context) {
    throw new Error('ChartContent must be used within a ChartProvider');
  }

  return context;
};

type ChartProviderProps = {
  data: TradingChartData;
  children: React.ReactNode;
};

export const ChartContextProvider: React.FC<ChartProviderProps> = ({
  data,
  children
}) => {
  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const indicatorContainerRefs =
    useRef<ChartContext['indicatorContainerRefs']['current']>(null);
  const indicatorChartRefs = useRef<Record<string, IChartApi[]>>({});
  const indicatorSeriesRefs = useRef<Partial<Record<string, any[] | null>>>({});
  const overlayIndicatorSeriesRefs = useRef<
    Partial<Record<string, any[] | null>>
  >({});
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [chartInitialized, setChartInitialized] = useState(false);
  const subplotChartCreatedSubject = useMemo(() => new Subject<void>(), []);

  useEffect(() => {
    return () => {
      subplotChartCreatedSubject.complete();
    };
  }, [subplotChartCreatedSubject]);

  const setContext = useCallback((update: Partial<ChartContext>) => {
    if (typeof update.initialLoad === 'boolean') {
      setInitialLoad(update.initialLoad);
    }
    if (typeof update.chartInitialized === 'boolean') {
      setChartInitialized(update.chartInitialized);
    }
  }, []);
  const emitSubplotChartCreated = useCallback(() => {
    subplotChartCreatedSubject.next();
  }, [subplotChartCreatedSubject]);
  const subplotChartCreated$ = useMemo(
    () => subplotChartCreatedSubject.asObservable(),
    [subplotChartCreatedSubject]
  );

  const value = useMemo<ChartContext>(
    () => ({
      chartContainerRef,
      indicatorContainerRefs,
      chartRef,
      candlestickSeriesRef,
      overlayIndicatorSeriesRefs,
      indicatorChartRefs,
      indicatorSeriesRefs,
      tooltipRef,
      initialLoad,
      chartInitialized,
      setContext,
      subplotChartCreated$,
      emitSubplotChartCreated,
      data
    }),
    [
      chartInitialized,
      data,
      emitSubplotChartCreated,
      initialLoad,
      setContext,
      subplotChartCreated$
    ]
  );

  return (
    <ChartContextInstance.Provider value={value}>
      {children}
    </ChartContextInstance.Provider>
  );
};
